/**
 * Supabase Database Service Layer
 * Handles all CRUD operations for events, clients, payments, activity log, and sent emails.
 * Maps between the app's camelCase field names and the database's snake_case columns.
 */
import { supabase } from './supabase';

// ─── HELPERS: pagination ───

const PAGE_SIZE = 1000;

/**
 * Fetch every row a query matches, paginating past PostgREST's 1000-row
 * default page cap.
 *
 * ROOT CAUSE (found 22 Aug 2026): a plain `.select('*')` with no `.range()`
 * silently returns only the first 1000 rows — Postgres/PostgREST returns
 * HTTP 206 (partial content) for the truncated page, which supabase-js does
 * NOT surface as an error, so the app just receives an incomplete array
 * with no warning anywhere. table_bookings crossed 1000 rows and this is
 * exactly why a real, successfully-saved booking (Peter Dempsey, R&R,
 * 2026-09-05, 35 covers) was completely invisible on the Tables page while
 * genuinely present in the database — confirmed live via a direct query
 * that showed content-range "0-999/1025". Any other table that grows past
 * 1000 rows will silently hit the same wall (spa_bookings is already at
 * 580+ as of this fix).
 *
 * `buildQuery` must return a fresh Supabase query builder each call (so we
 * can re-apply `.range()` per page) and should end with a stable `.order()`
 * — pagination over an unstable sort can skip or repeat rows across pages,
 * so callers add a unique tiebreaker (id) as a secondary sort key.
 */
export async function fetchAllRows(buildQuery) {
  let rows = [];
  let from = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await buildQuery().range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    rows = rows.concat(data || []);
    if (!data || data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return rows;
}

// ─── HELPERS: field mapping ───

/** Convert a DB event row to the app's camelCase format */
function dbEventToApp(row) {
  return {
    id: row.id,
    name: row.name || '',
    client: row.client_name || '',
    clientPhone: row.client_phone || '',
    clientEmail: row.client_email || '',
    clientId: row.client_id || null,
    type: row.type || 'corporate',
    venue_id: row.venue_id || '',
    date: row.date || '',
    endDate: row.end_date || null,
    startTime: row.start_time || '',
    endTime: row.end_time || '',
    guests: row.guests || 0,
    stage: row.stage || 'enquiry',
    setup: row.setup || '',
    equipment: row.equipment || [],
    fbItems: row.fb_items || [],
    services: row.services || [],
    roomHire: parseFloat(row.room_hire) || 0,
    pricePerHead: parseFloat(row.price_per_head) || 0,
    packageTotal: parseFloat(row.package_total) || 0,
    depositPaid: parseFloat(row.deposit_paid) || 0,
    notes: row.notes || '',
    billingType: row.billing_type || 'group',
    consumption: row.consumption != null ? parseFloat(row.consumption) : null,
    gcal_id: row.gcal_id || '',
    gcal_calendar: row.gcal_calendar || '',
    subtype: row.subtype || '',
    setupDescription: row.setup_description || '',
    contractSigned: row.contract_signed || false,
    contractDate: row.contract_date || '',
    dietaryRequirements: row.dietary_requirements || '',
    setupRequirements: row.setup_requirements || '',
    menuNotes: row.menu_notes || '',
    dietaryVeg: row.dietary_veg ?? null,
    dietaryVegan: row.dietary_vegan ?? null,
    dietaryCoeliac: row.dietary_coeliac ?? null,
    dietaryAllergens: row.dietary_allergens || '',
    foodServiceTime: row.food_service_time || '',
    dietaryNA: !!row.dietary_na,
    musicOnly: !!row.music_only,
    attachments: row.attachments || [],
    brideName: row.bride_name || '',
    groomName: row.groom_name || '',
    weddingDetails: row.wedding_details || null,
    contractUrl: row.contract_url || '',
    contractSignedUrl: row.contract_signed_url || '',
    contractSignedAt: row.contract_signed_at || '',
    alertsDismissed: row.alerts_dismissed || [],
    previousStage: row.previous_stage || null,
    invoiceNumber: row.invoice_number || '',
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** Convert an app event object to DB snake_case format */
function appEventToDb(evt) {
  const row = {};
  if (evt.name !== undefined) row.name = evt.name;
  if (evt.client !== undefined) row.client_name = evt.client;
  if (evt.clientPhone !== undefined) row.client_phone = evt.clientPhone;
  if (evt.clientEmail !== undefined) row.client_email = evt.clientEmail;
  if (evt.clientId !== undefined) row.client_id = evt.clientId;
  if (evt.type !== undefined) row.type = evt.type;
  if (evt.venue_id !== undefined) row.venue_id = evt.venue_id;
  if (evt.date !== undefined) row.date = evt.date;
  if (evt.endDate !== undefined) row.end_date = evt.endDate || null;
  if (evt.startTime !== undefined) row.start_time = evt.startTime;
  if (evt.endTime !== undefined) row.end_time = evt.endTime;
  if (evt.guests !== undefined) row.guests = evt.guests;
  if (evt.stage !== undefined) row.stage = evt.stage;
  if (evt.setup !== undefined) row.setup = evt.setup;
  if (evt.equipment !== undefined) row.equipment = evt.equipment;
  if (evt.fbItems !== undefined) row.fb_items = evt.fbItems;
  if (evt.services !== undefined) row.services = evt.services;
  if (evt.roomHire !== undefined) row.room_hire = evt.roomHire;
  if (evt.pricePerHead !== undefined) row.price_per_head = evt.pricePerHead;
  if (evt.packageTotal !== undefined) row.package_total = evt.packageTotal;
  if (evt.depositPaid !== undefined) row.deposit_paid = typeof evt.depositPaid === 'boolean' ? (evt.depositPaid ? 1 : 0) : (evt.depositPaid || 0);
  if (evt.notes !== undefined) row.notes = evt.notes;
  if (evt.billingType !== undefined) row.billing_type = evt.billingType;
  if (evt.consumption !== undefined) row.consumption = evt.consumption;
  if (evt.gcal_id !== undefined) row.gcal_id = evt.gcal_id;
  if (evt.gcal_calendar !== undefined) row.gcal_calendar = evt.gcal_calendar;
  if (evt.subtype !== undefined) row.subtype = evt.subtype;
  if (evt.setupDescription !== undefined) row.setup_description = evt.setupDescription;
  if (evt.contractSigned !== undefined) row.contract_signed = evt.contractSigned;
  if (evt.contractDate !== undefined) row.contract_date = evt.contractDate || null;
  if (evt.dietaryRequirements !== undefined) row.dietary_requirements = evt.dietaryRequirements;
  if (evt.setupRequirements !== undefined) row.setup_requirements = evt.setupRequirements;
  if (evt.menuNotes !== undefined) row.menu_notes = evt.menuNotes;
  const _int = (v) => (v === '' || v === null || v === undefined || isNaN(parseInt(v, 10)) ? null : parseInt(v, 10));
  if (evt.dietaryVeg !== undefined) row.dietary_veg = _int(evt.dietaryVeg);
  if (evt.dietaryVegan !== undefined) row.dietary_vegan = _int(evt.dietaryVegan);
  if (evt.dietaryCoeliac !== undefined) row.dietary_coeliac = _int(evt.dietaryCoeliac);
  if (evt.dietaryAllergens !== undefined) row.dietary_allergens = evt.dietaryAllergens || null;
  if (evt.foodServiceTime !== undefined) row.food_service_time = evt.foodServiceTime || null;
  if (evt.dietaryNA !== undefined) row.dietary_na = !!evt.dietaryNA;
  if (evt.musicOnly !== undefined) row.music_only = !!evt.musicOnly;
  if (evt.attachments !== undefined) row.attachments = evt.attachments;
  if (evt.brideName !== undefined) row.bride_name = evt.brideName;
  if (evt.groomName !== undefined) row.groom_name = evt.groomName;
  if (evt.weddingDetails !== undefined) row.wedding_details = evt.weddingDetails;
  if (evt.contractUrl !== undefined) row.contract_url = evt.contractUrl;
  if (evt.contractSignedUrl !== undefined) row.contract_signed_url = evt.contractSignedUrl;
  if (evt.contractSignedAt !== undefined) row.contract_signed_at = evt.contractSignedAt || null;
  if (evt.alertsDismissed !== undefined) row.alerts_dismissed = evt.alertsDismissed;
  if (evt.previousStage !== undefined) row.previous_stage = evt.previousStage;
  if (evt.invoiceNumber !== undefined) row.invoice_number = evt.invoiceNumber || null;
  return row;
}

/** Convert a DB client row to app format */
function dbClientToApp(row) {
  return {
    id: row.id,
    name: row.name || '',
    company: row.company || '',
    phone: row.phone || '',
    email: row.email || '',
    tag: row.tag || '',
    totalEvents: row.total_events || 0,
    totalRevenue: parseFloat(row.total_revenue) || 0,
    lastEvent: row.last_event || '',
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** Convert app client to DB format */
function appClientToDb(c) {
  const row = {};
  if (c.name !== undefined) row.name = c.name;
  if (c.company !== undefined) row.company = c.company;
  if (c.phone !== undefined) row.phone = c.phone;
  if (c.email !== undefined) row.email = c.email;
  if (c.tag !== undefined) row.tag = c.tag;
  if (c.totalEvents !== undefined) row.total_events = c.totalEvents;
  if (c.totalRevenue !== undefined) row.total_revenue = c.totalRevenue;
  if (c.lastEvent !== undefined) row.last_event = c.lastEvent || null;
  return row;
}

/** Convert a DB payment row to app format */
function dbPaymentToApp(row) {
  return {
    id: row.id,
    event_id: row.event_id || '',    // keep snake_case — used throughout the app
    eventId: row.event_id || '',      // camelCase alias for any code that uses it
    booking_id: row.booking_id || '',
    bookingId: row.booking_id || '',
    amount: parseFloat(row.amount) || 0,
    payment_date: row.payment_date || '',
    paymentType: row.payment_type || 'bank_transfer',
    category: row.category || 'package',  // deposit | room_hire | fb_package | final_balance | other
    description: row.description || '',
    status: row.status || 'completed',
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** Convert app payment to DB format */
function appPaymentToDb(p) {
  const row = {};
  if (p.event_id !== undefined || p.eventId !== undefined) row.event_id = p.event_id || p.eventId || null;
  if (p.booking_id !== undefined || p.bookingId !== undefined) row.booking_id = p.booking_id || p.bookingId || null;
  if (p.amount !== undefined) row.amount = p.amount;
  if (p.payment_date !== undefined) row.payment_date = p.payment_date;
  if (p.paymentType !== undefined) row.payment_type = p.paymentType;
  if (p.category !== undefined) row.category = p.category;
  if (p.description !== undefined) row.description = p.description;
  if (p.status !== undefined) row.status = p.status;
  return row;
}

/** Convert a DB sent_email row to app format */
function dbSentEmailToApp(row) {
  return {
    id: row.id,
    eventId: row.event_id || '',
    eventName: row.event_name || '',
    templateId: row.template_id || '',
    templateName: row.template_name || '',
    to: row.recipient || '',
    subject: row.subject || '',
    body: row.body || '',
    htmlBody: row.html_body || '',
    direction: row.direction || 'outbound',
    from: row.from_address || 'events@errigalhotel.com',
    status: row.status || 'sent',
    auto: row.auto || false,
    reviewed: row.reviewed || false,
    trackingId: row.tracking_id || '',
    tracking: row.tracking || {},
    attachments: row.attachments || [],
    sentAt: row.sent_at,
  };
}

/** Convert a DB activity_log row to app format */
function dbActivityToApp(row) {
  const ts = new Date(row.created_at);
  return {
    userId: row.user_id,
    userName: row.user_name || 'Unknown',
    userInitials: row.user_initials || '?',
    colour: row.colour || '#6B7280',
    action: row.action,
    time: ts.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' }) +
          ' · ' + ts.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' }),
    timestamp: row.created_at,
  };
}


// ─── CHECK if Supabase is configured ───

export function isSupabaseReady() {
  const url = process.env.REACT_APP_SUPABASE_URL || '';
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
  return url.length > 0 && key.length > 0;
}


// ═══════════════════════════════════════════
// EVENTS
// ═══════════════════════════════════════════

export async function fetchEvents() {
  const data = await fetchAllRows(() =>
    supabase.from('events').select('*').order('date', { ascending: true }).order('id', { ascending: true })
  );
  return data.map(dbEventToApp);
}

export async function insertEvent(eventData) {
  const row = appEventToDb(eventData);
  const { data, error } = await supabase
    .from('events')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return dbEventToApp(data);
}

export async function updateEventDb(eventId, updates) {
  const row = appEventToDb(updates);
  const { data, error } = await supabase
    .from('events')
    .update(row)
    .eq('id', eventId)
    .select()
    .single();
  if (error) throw error;
  return dbEventToApp(data);
}

export async function deleteEventDb(eventId) {
  const { error } = await supabase
    .from('events')
    .delete()
    .eq('id', eventId);
  if (error) throw error;
}

/**
 * Subscribe to live changes on events, mirroring subscribeToTableBookings
 * below. Without this, an event edited/added/deleted on one device (guest
 * count, dietary requirements, stage) is invisible on another open session —
 * including a printed function sheet — until a manual reload. Requires the
 * table in the supabase_realtime publication (migration
 * enable_realtime_events_spa_bookings). Returns an unsubscribe function.
 */
export function subscribeToEvents({ onInsert, onUpdate, onDelete } = {}) {
  const channel = supabase
    .channel('events_live')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'events' },
      (payload) => { if (onInsert && payload.new) onInsert(dbEventToApp(payload.new)); })
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'events' },
      (payload) => { if (onUpdate && payload.new) onUpdate(dbEventToApp(payload.new)); })
    .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'events' },
      (payload) => { if (onDelete && payload.old) onDelete(payload.old.id); })
    .subscribe();
  return () => { try { supabase.removeChannel(channel); } catch (_) { /* no-op */ } };
}

/**
 * Atomically append a note to an event's notes field via the
 * append_event_note DB function (migration add_atomic_append_event_note).
 * Doing the append as one UPDATE ... RETURNING inside Postgres, rather than
 * reading notes into JS and writing the whole string back, closes the race
 * where two staff adding a note around the same time can have one save
 * silently clobber the other. Returns the full notes string after the
 * append, which is the source of truth — not whatever this client's stale
 * local copy predicted it would be.
 */
export async function appendEventNoteDb(eventId, noteText) {
  const { data, error } = await supabase.rpc('append_event_note', {
    p_event_id: eventId,
    p_note_text: noteText,
  });
  if (error) throw error;
  return data;
}


// ═══════════════════════════════════════════
// CLIENTS
// ═══════════════════════════════════════════

export async function fetchClients() {
  const data = await fetchAllRows(() =>
    supabase.from('clients').select('*').order('name', { ascending: true }).order('id', { ascending: true })
  );
  return data.map(dbClientToApp);
}

export async function insertClient(clientData) {
  const row = appClientToDb(clientData);
  const { data, error } = await supabase
    .from('clients')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return dbClientToApp(data);
}

export async function updateClientDb(clientId, updates) {
  const row = appClientToDb(updates);
  const { data, error } = await supabase
    .from('clients')
    .update(row)
    .eq('id', clientId)
    .select()
    .single();
  if (error) throw error;
  return dbClientToApp(data);
}

export async function deleteClientDb(clientId) {
  const { error } = await supabase
    .from('clients')
    .delete()
    .eq('id', clientId);
  if (error) throw error;
}

/**
 * Subscribe to live changes on clients, completing the realtime rollout
 * (events, spa_bookings, table_bookings already covered). Requires the
 * table in the supabase_realtime publication (migration
 * enable_realtime_clients_payments_tasks). Returns an unsubscribe function.
 */
export function subscribeToClients({ onInsert, onUpdate, onDelete } = {}) {
  const channel = supabase
    .channel('clients_live')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'clients' },
      (payload) => { if (onInsert && payload.new) onInsert(dbClientToApp(payload.new)); })
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'clients' },
      (payload) => { if (onUpdate && payload.new) onUpdate(dbClientToApp(payload.new)); })
    .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'clients' },
      (payload) => { if (onDelete && payload.old) onDelete(payload.old.id); })
    .subscribe();
  return () => { try { supabase.removeChannel(channel); } catch (_) { /* no-op */ } };
}


// ═══════════════════════════════════════════
// PAYMENTS
// ═══════════════════════════════════════════

export async function fetchPayments() {
  const data = await fetchAllRows(() =>
    supabase.from('payments').select('*').order('payment_date', { ascending: false }).order('id', { ascending: true })
  );
  return data.map(dbPaymentToApp);
}

export async function insertPayment(paymentData) {
  const row = appPaymentToDb(paymentData);
  const { data, error } = await supabase
    .from('payments')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return dbPaymentToApp(data);
}

export async function updatePaymentDb(paymentId, updates) {
  const row = appPaymentToDb(updates);
  const { data, error } = await supabase
    .from('payments')
    .update(row)
    .eq('id', paymentId)
    .select()
    .single();
  if (error) throw error;
  return dbPaymentToApp(data);
}

export async function deletePaymentDb(paymentId) {
  const { error } = await supabase
    .from('payments')
    .delete()
    .eq('id', paymentId);
  if (error) throw error;
}

/**
 * Subscribe to live changes on payments. Requires the table in the
 * supabase_realtime publication (migration
 * enable_realtime_clients_payments_tasks). Returns an unsubscribe function.
 */
export function subscribeToPayments({ onInsert, onUpdate, onDelete } = {}) {
  const channel = supabase
    .channel('payments_live')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'payments' },
      (payload) => { if (onInsert && payload.new) onInsert(dbPaymentToApp(payload.new)); })
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'payments' },
      (payload) => { if (onUpdate && payload.new) onUpdate(dbPaymentToApp(payload.new)); })
    .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'payments' },
      (payload) => { if (onDelete && payload.old) onDelete(payload.old.id); })
    .subscribe();
  return () => { try { supabase.removeChannel(channel); } catch (_) { /* no-op */ } };
}


// ═══════════════════════════════════════════
// TABLE BOOKINGS
// ═══════════════════════════════════════════

/** Convert a DB table_booking row to app format */
function dbTableBookingToApp(row) {
  return {
    id: row.id,
    guestName: row.guest_name || '',
    phone: row.phone || '',
    email: row.email || '',
    venue: row.venue || '',
    date: row.date || '',
    time: row.time || '',
    covers: row.covers || 0,
    specialRequirements: row.special_requirements || '',
    status: row.status || 'confirmed',
    source: row.source || 'web',
    partyComposition: row.party_composition || '',
    roomNumber: row.room_number || '',
    depositAmount: parseFloat(row.deposit_amount) || 0,
    depositPaid: row.deposit_paid || false,
    stripePaymentId: row.stripe_payment_id || '',
    stripeSessionId: row.stripe_session_id || '',
    tableNumber: row.table_number || '',
    notes: row.notes || '',
    seatedAt: row.seated_at || null,
    completedAt: row.completed_at || null,
    linkedEventId: row.linked_event_id || null,
    linkedAt: row.linked_at || null,
    packageName: row.package_name || '',
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** Convert app table booking to DB format */
function appTableBookingToDb(b) {
  const row = {};
  if (b.guestName !== undefined) row.guest_name = b.guestName;
  if (b.phone !== undefined) row.phone = b.phone;
  if (b.email !== undefined) row.email = b.email;
  if (b.venue !== undefined) row.venue = b.venue;
  if (b.date !== undefined) row.date = b.date;
  if (b.time !== undefined) row.time = b.time;
  if (b.covers !== undefined) row.covers = parseInt(b.covers) || 0;
  if (b.specialRequirements !== undefined) row.special_requirements = b.specialRequirements;
  if (b.status !== undefined) row.status = b.status;
  if (b.source !== undefined) row.source = b.source;
  if (b.partyComposition !== undefined) row.party_composition = b.partyComposition;
  if (b.roomNumber !== undefined) row.room_number = b.roomNumber;
  if (b.depositAmount !== undefined) row.deposit_amount = b.depositAmount;
  if (b.depositPaid !== undefined) row.deposit_paid = b.depositPaid;
  if (b.stripePaymentId !== undefined) row.stripe_payment_id = b.stripePaymentId;
  if (b.stripeSessionId !== undefined) row.stripe_session_id = b.stripeSessionId;
  if (b.tableNumber !== undefined) row.table_number = b.tableNumber;
  if (b.notes !== undefined) row.notes = b.notes;
  if (b.seatedAt !== undefined) row.seated_at = b.seatedAt;
  if (b.completedAt !== undefined) row.completed_at = b.completedAt;
  if (b.linkedEventId !== undefined) row.linked_event_id = b.linkedEventId;
  if (b.linkedAt !== undefined) row.linked_at = b.linkedAt;
  if (b.packageName !== undefined) row.package_name = b.packageName || null;
  return row;
}

export async function fetchTableBookings() {
  const data = await fetchAllRows(() =>
    supabase.from('table_bookings').select('*').order('date', { ascending: true }).order('id', { ascending: true })
  );
  return data.map(dbTableBookingToApp);
}

export async function insertTableBooking(bookingData) {
  const row = appTableBookingToDb(bookingData);
  const { data, error } = await supabase
    .from('table_bookings')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return dbTableBookingToApp(data);
}

export async function updateTableBookingDb(bookingId, updates) {
  const row = appTableBookingToDb(updates);
  const { data, error } = await supabase
    .from('table_bookings')
    .update(row)
    .eq('id', bookingId)
    .select()
    .single();
  if (error) throw error;
  return dbTableBookingToApp(data);
}

export async function deleteTableBookingDb(bookingId) {
  const { error } = await supabase
    .from('table_bookings')
    .delete()
    .eq('id', bookingId);
  if (error) throw error;
}

/** Fetch availability for a specific venue and date (used by public widget) */
export async function fetchTableAvailability(venue, date) {
  const { data, error } = await supabase
    .from('table_bookings')
    .select('time, covers, status')
    .eq('venue', venue)
    .eq('date', date)
    .not('status', 'in', '("cancelled","no_show")');
  if (error) throw error;
  return data || [];
}

/**
 * Subscribe to live changes on table_bookings so an already-open staff session
 * picks up bookings made elsewhere (the website widget, another device) without
 * a manual page reload. Requires the table in the supabase_realtime publication
 * (migration 021). Realtime respects RLS: authenticated staff receive every row,
 * the anon widget key does not. If realtime isn't enabled the channel never
 * fires - safe, additive. Returns an unsubscribe function.
 */
export function subscribeToTableBookings({ onInsert, onUpdate, onDelete } = {}) {
  const channel = supabase
    .channel('table_bookings_live')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'table_bookings' },
      (payload) => { if (onInsert && payload.new) onInsert(dbTableBookingToApp(payload.new)); })
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'table_bookings' },
      (payload) => { if (onUpdate && payload.new) onUpdate(dbTableBookingToApp(payload.new)); })
    .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'table_bookings' },
      (payload) => { if (onDelete && payload.old) onDelete(payload.old.id); })
    .subscribe();
  return () => { try { supabase.removeChannel(channel); } catch (_) { /* no-op */ } };
}


// ═══════════════════════════════════════════
// SENT EMAILS
// ═══════════════════════════════════════════

export async function fetchSentEmails() {
  const { data, error } = await supabase
    .from('sent_emails')
    .select('*')
    .order('sent_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data || []).map(dbSentEmailToApp);
}

export async function insertSentEmail(emailData) {
  const row = {
    event_id: emailData.eventId || null,
    event_name: emailData.eventName || '',
    template_id: emailData.templateId || '',
    template_name: emailData.templateName || '',
    recipient: emailData.to || '',
    subject: emailData.subject || '',
    body: emailData.body || '',
    status: emailData.status || 'sent',
    auto: emailData.auto || false,
    reviewed: emailData.reviewed || false,
    tracking_id: emailData.trackingId || '',
    tracking: emailData.tracking || {},
    attachments: emailData.attachments || [],
  };
  if (emailData.htmlBody) row.html_body = emailData.htmlBody;
  if (emailData.direction) row.direction = emailData.direction;
  if (emailData.from) row.from_address = emailData.from;
  const { data, error } = await supabase
    .from('sent_emails')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return dbSentEmailToApp(data);
}

export async function updateSentEmailDb(emailId, updates) {
  const row = {};
  if (updates.status !== undefined) row.status = updates.status;
  if (updates.tracking !== undefined) row.tracking = updates.tracking;
  const { data, error } = await supabase
    .from('sent_emails')
    .update(row)
    .eq('id', emailId)
    .select()
    .single();
  if (error) throw error;
  return dbSentEmailToApp(data);
}


// ═══════════════════════════════════════════
// INBOUND EMAILS
// ═══════════════════════════════════════════

/**
 * Map a raw `inbound_emails` DB row (snake_case) into the camelCase shape
 * the UI uses. Without this, components that read `email.from` / `email.fromName`
 * / `email.bodyText` would all resolve to undefined — which is why Jacqueline
 * saw "From: undefined <undefined@undefined>" on 22 Apr 2026.
 *
 * We preserve both forms (camelCase primary + snake_case mirror) so that any
 * component still reading the old snake_case field keeps working.
 */
function dbInboundEmailToApp(row) {
  if (!row) return row;
  return {
    id: row.id,
    messageId: row.message_id || '',
    uid: row.uid,
    from: row.from_address || '',
    fromAddress: row.from_address || '',
    fromName: row.from_name || '',
    to: row.to_address || '',
    toAddress: row.to_address || '',
    subject: row.subject || '',
    bodyText: row.body_text || '',
    bodyHtml: row.body_html || '',
    snippet: row.snippet || '',
    date: row.date,
    receivedAt: row.date,
    isRead: !!row.is_read,
    isStarred: !!row.is_starred,
    isProcessed: !!row.is_processed,
    processedEventId: row.processed_event_id || null,
    eventId: row.processed_event_id || null,
    processedBookingId: row.processed_booking_id || null,
    bookingId: row.processed_booking_id || null,
    labels: row.labels || [],
    hasAttachments: !!row.has_attachments,
    attachmentNames: row.attachment_names || [],
    rawHeaders: row.raw_headers || {},
    direction: 'inbound',
    // Keep original snake_case fields too — some components still use them.
    from_address: row.from_address || '',
    from_name: row.from_name || '',
    to_address: row.to_address || '',
    body_text: row.body_text || '',
    body_html: row.body_html || '',
    is_read: !!row.is_read,
    has_attachments: !!row.has_attachments,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

// List-only fields — excludes body_html/body_text/raw_headers (can be 100s of KB per email).
// Full body is fetched on-demand by fetchInboundEmailById when staff open an email.
// This runs every 2 minutes so keeping it lean is critical for Supabase egress.
const INBOUND_EMAIL_LIST_FIELDS = [
  'id', 'message_id', 'uid',
  'from_address', 'from_name', 'to_address',
  'subject', 'snippet',
  'date', 'created_at', 'updated_at',
  'is_read', 'is_starred', 'is_processed',
  'processed_event_id', 'processed_booking_id',
  'labels', 'has_attachments', 'attachment_names',
].join(', ');

export async function fetchInboundEmails() {
  const { data, error } = await supabase
    .from('inbound_emails')
    .select(INBOUND_EMAIL_LIST_FIELDS)
    .order('date', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data || []).map(dbInboundEmailToApp);
}

export async function fetchInboundEmailById(emailId) {
  const { data, error } = await supabase
    .from('inbound_emails')
    .select('*')
    .eq('id', emailId)
    .single();
  if (error) throw error;
  return dbInboundEmailToApp(data);
}

export async function markInboundEmailRead(emailId) {
  const { error } = await supabase
    .from('inbound_emails')
    .update({ is_read: true })
    .eq('id', emailId);
  if (error) throw error;
}

export async function linkInboundEmailToEvent(emailId, eventId) {
  const { error } = await supabase
    .from('inbound_emails')
    .update({ processed_event_id: eventId, is_processed: true })
    .eq('id', emailId);
  if (error) throw error;
}

/**
 * Link an inbound email to a table booking. Added in migration 015 —
 * some enquiries land in the inbox but become a table booking rather
 * than an event, and staff want to attach the email to the booking
 * for history.
 */
export async function linkInboundEmailToBooking(emailId, bookingId) {
  const { error } = await supabase
    .from('inbound_emails')
    .update({ processed_booking_id: bookingId, is_processed: true })
    .eq('id', emailId);
  if (error) throw error;
}

export async function archiveInboundEmail(emailId) {
  const { error } = await supabase
    .from('inbound_emails')
    .update({ is_processed: true })
    .eq('id', emailId);
  if (error) throw error;
}


// ═══════════════════════════════════════════
// ACTIVITY LOG
// ═══════════════════════════════════════════

export async function fetchActivityLog() {
  const { data, error } = await supabase
    .from('activity_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data || []).map(dbActivityToApp);
}

export async function insertActivity(entry) {
  const row = {
    user_id: entry.userId || '',
    user_name: entry.userName || 'Unknown',
    user_initials: entry.userInitials || '?',
    colour: entry.colour || '#6B7280',
    action: entry.action,
  };
  const { data, error } = await supabase
    .from('activity_log')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return dbActivityToApp(data);
}


// ═══════════════════════════════════════════
// EVENT ATTACHMENTS (Supabase Storage)
// ═══════════════════════════════════════════

const ATTACHMENT_BUCKET = 'event-attachments';

/** Upload a file to Supabase Storage and return its metadata */
export async function uploadAttachment(eventId, file) {
  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const filePath = `${eventId}/${timestamp}_${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(ATTACHMENT_BUCKET)
    .upload(filePath, file, { upsert: false });

  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage
    .from(ATTACHMENT_BUCKET)
    .getPublicUrl(filePath);

  return {
    id: `${timestamp}_${safeName}`,
    name: file.name,
    size: file.size,
    type: file.type,
    path: filePath,
    url: urlData.publicUrl,
    uploadedAt: new Date().toISOString(),
  };
}

/** Delete a file from Supabase Storage */
export async function deleteAttachment(filePath) {
  const { error } = await supabase.storage
    .from(ATTACHMENT_BUCKET)
    .remove([filePath]);
  if (error) throw error;
}

/** Get a signed download URL for a private file */
export async function getAttachmentUrl(filePath) {
  const { data, error } = await supabase.storage
    .from(ATTACHMENT_BUCKET)
    .createSignedUrl(filePath, 3600); // 1 hour expiry
  if (error) throw error;
  return data.signedUrl;
}


// ═══════════════════════════════════════════
// EVENT TASKS
// ═══════════════════════════════════════════

function dbTaskToApp(row) {
  return {
    id: row.id,
    eventId: row.event_id || null,
    title: row.title || '',
    assignedTo: row.assigned_to || null,
    assignedToName: row.assigned_to_name || '',
    createdBy: row.created_by || null,
    createdByName: row.created_by_name || '',
    dueDate: row.due_date || null,
    completed: row.completed || false,
    completedAt: row.completed_at || null,
    priority: row.priority || 'normal',
    autoType: row.auto_type || null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function appTaskToDb(task) {
  const row = {};
  if (task.eventId !== undefined) row.event_id = task.eventId || null;
  if (task.title !== undefined) row.title = task.title;
  if (task.assignedTo !== undefined) row.assigned_to = task.assignedTo;
  if (task.assignedToName !== undefined) row.assigned_to_name = task.assignedToName;
  if (task.createdBy !== undefined) row.created_by = task.createdBy;
  if (task.createdByName !== undefined) row.created_by_name = task.createdByName;
  if (task.dueDate !== undefined) row.due_date = task.dueDate || null;
  if (task.completed !== undefined) row.completed = task.completed;
  if (task.completedAt !== undefined) row.completed_at = task.completedAt;
  if (task.priority !== undefined) row.priority = task.priority;
  if (task.autoType !== undefined) row.auto_type = task.autoType || null;
  return row;
}

export async function fetchEventTasks() {
  const data = await fetchAllRows(() =>
    supabase.from('event_tasks').select('*').order('created_at', { ascending: false }).order('id', { ascending: true })
  );
  return data.map(dbTaskToApp);
}

export async function insertEventTask(taskData) {
  const row = appTaskToDb(taskData);
  const { data, error } = await supabase
    .from('event_tasks')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return dbTaskToApp(data);
}

export async function updateEventTaskDb(taskId, updates) {
  const row = appTaskToDb(updates);
  row.updated_at = new Date().toISOString();
  const { data, error } = await supabase
    .from('event_tasks')
    .update(row)
    .eq('id', taskId)
    .select()
    .single();
  if (error) throw error;
  return dbTaskToApp(data);
}

export async function deleteEventTaskDb(taskId) {
  const { error } = await supabase
    .from('event_tasks')
    .delete()
    .eq('id', taskId);
  if (error) throw error;
}

/**
 * Subscribe to live changes on event_tasks. Requires the table in the
 * supabase_realtime publication (migration
 * enable_realtime_clients_payments_tasks). Returns an unsubscribe function.
 */
export function subscribeToEventTasks({ onInsert, onUpdate, onDelete } = {}) {
  const channel = supabase
    .channel('event_tasks_live')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'event_tasks' },
      (payload) => { if (onInsert && payload.new) onInsert(dbTaskToApp(payload.new)); })
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'event_tasks' },
      (payload) => { if (onUpdate && payload.new) onUpdate(dbTaskToApp(payload.new)); })
    .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'event_tasks' },
      (payload) => { if (onDelete && payload.old) onDelete(payload.old.id); })
    .subscribe();
  return () => { try { supabase.removeChannel(channel); } catch (_) { /* no-op */ } };
}

// ═══════════════════════════════════════════
// SPA BOOKINGS
// ═══════════════════════════════════════════

function dbSpaToApp(row) {
  return {
    id: row.id,
    date: row.date || null,
    time: row.time || '',
    guestName: row.guest_name || '',
    details: row.details || '',
    covers: row.covers != null ? Number(row.covers) : null,
    deposit: row.deposit != null ? Number(row.deposit) : null,
    comments: row.comments || '',
    roomNumber: row.room_number || '',
    duration: row.duration || 60,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function appSpaToDb(b) {
  const row = {};
  if (b.date !== undefined) row.date = b.date || null;
  if (b.time !== undefined) row.time = b.time || null;
  if (b.guestName !== undefined) row.guest_name = b.guestName || null;
  if (b.details !== undefined) row.details = b.details || null;
  if (b.covers !== undefined) row.covers = b.covers === '' || b.covers == null ? null : Number(b.covers);
  if (b.deposit !== undefined) row.deposit = b.deposit === '' || b.deposit == null ? null : Number(b.deposit);
  if (b.comments !== undefined) row.comments = b.comments || null;
  if (b.roomNumber !== undefined) row.room_number = b.roomNumber || null;
  if (b.duration !== undefined) row.duration = b.duration || 60;
  return row;
}

export async function fetchSpaBookings() {
  const data = await fetchAllRows(() =>
    supabase
      .from('spa_bookings')
      .select('*')
      .order('date', { ascending: false })
      .order('time', { ascending: true })
      .order('id', { ascending: true })
  );
  return data.map(dbSpaToApp);
}

export async function insertSpaBooking(bookingData) {
  const row = appSpaToDb(bookingData);
  const { data, error } = await supabase
    .from('spa_bookings')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return dbSpaToApp(data);
}

export async function updateSpaBookingDb(bookingId, updates) {
  const row = appSpaToDb(updates);
  row.updated_at = new Date().toISOString();
  const { data, error } = await supabase
    .from('spa_bookings')
    .update(row)
    .eq('id', bookingId)
    .select()
    .single();
  if (error) throw error;
  return dbSpaToApp(data);
}

export async function deleteSpaBookingDb(bookingId) {
  const { error } = await supabase
    .from('spa_bookings')
    .delete()
    .eq('id', bookingId);
  if (error) throw error;
}

/**
 * Subscribe to live changes on spa_bookings, mirroring subscribeToTableBookings.
 * Spa bookings feed the printed function sheets alongside events and table
 * bookings, so without this a spa booking added/changed on another device is
 * missing from an already-open sheet until reload. Requires the table in the
 * supabase_realtime publication (migration enable_realtime_events_spa_bookings).
 * Returns an unsubscribe function.
 */
export function subscribeToSpaBookings({ onInsert, onUpdate, onDelete } = {}) {
  const channel = supabase
    .channel('spa_bookings_live')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'spa_bookings' },
      (payload) => { if (onInsert && payload.new) onInsert(dbSpaToApp(payload.new)); })
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'spa_bookings' },
      (payload) => { if (onUpdate && payload.new) onUpdate(dbSpaToApp(payload.new)); })
    .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'spa_bookings' },
      (payload) => { if (onDelete && payload.old) onDelete(payload.old.id); })
    .subscribe();
  return () => { try { supabase.removeChannel(channel); } catch (_) { /* no-op */ } };
}

// ═══════════════════════════════════════════
// HOTEL OCCUPANCY CACHE
// ═══════════════════════════════════════════

function dbOccupancyToApp(row) {
  return {
    id:              row.id,
    date:            row.date || '',
    roomNumber:      row.room_number || '',
    guestName:       row.guest_name || '',
    adults:          row.adults ?? 1,
    children:        row.children || 0,
    packageName:     row.package_name || '',
    breakfastIncl:   row.breakfast_incl || false,
    dinnerIncl:      row.dinner_incl || false,
    spaAccess:       row.spa_access || false,
    source:          row.source || 'manual',
    clockBookingId:  row.clock_booking_id || null,
    syncedAt:        row.synced_at || null,
    createdAt:       row.created_at,
    updatedAt:       row.updated_at,
  };
}

function appOccupancyToDb(g) {
  const row = {};
  if (g.date         !== undefined) row.date             = g.date || null;
  if (g.roomNumber   !== undefined) row.room_number      = g.roomNumber || null;
  if (g.guestName    !== undefined) row.guest_name       = g.guestName || null;
  if (g.adults       !== undefined) row.adults           = parseInt(g.adults) || 1;
  if (g.children     !== undefined) row.children         = parseInt(g.children) || 0;
  if (g.packageName  !== undefined) row.package_name     = g.packageName || null;
  if (g.breakfastIncl !== undefined) row.breakfast_incl  = !!g.breakfastIncl;
  if (g.dinnerIncl   !== undefined) row.dinner_incl      = !!g.dinnerIncl;
  if (g.spaAccess    !== undefined) row.spa_access       = !!g.spaAccess;
  if (g.source       !== undefined) row.source           = g.source || 'manual';
  if (g.clockBookingId !== undefined) row.clock_booking_id = g.clockBookingId || null;
  if (g.syncedAt     !== undefined) row.synced_at        = g.syncedAt || null;
  return row;
}

/** Fetch all occupancy rows for a given date (YYYY-MM-DD) */
export async function fetchOccupancyByDate(date) {
  const { data, error } = await supabase
    .from('hotel_occupancy_cache')
    .select('*')
    .eq('date', date)
    .order('room_number', { ascending: true });
  if (error) throw error;
  return (data || []).map(dbOccupancyToApp);
}

// ═══════════════════════════════════════════
// VENUE DAY BLOCKS
// ═══════════════════════════════════════════

/**
 * Fetch all venue blocks for an array of dates (yyyy-MM-dd strings).
 * Returns an array of { id, venueId, date, sittingId, reason, blockType } objects.
 */
export async function fetchVenueBlocksByDates(dates) {
  if (!dates || dates.length === 0) return [];
  const { data, error } = await supabase
    .from('venue_day_blocks')
    .select('id, venue_id, date, sitting_id, reason, block_type')
    .in('date', dates);
  if (error) throw error;
  return (data || []).map((r) => ({
    id:        r.id,
    venueId:   r.venue_id,
    date:      r.date,
    sittingId: r.sitting_id,
    reason:    r.reason || '',
    blockType: r.block_type || 'closed',
  }));
}

/**
 * Block or override-open a venue sitting for a date.
 * sittingId defaults to 'all_day'.
 * blockType: 'closed' blocks the sitting; 'open' overrides schedule to allow bookings.
 */
export async function insertVenueBlock({ venueId, date, sittingId = 'all_day', reason = '', createdBy = '', blockType = 'closed' }) {
  const { data, error } = await supabase
    .from('venue_day_blocks')
    .upsert(
      { venue_id: venueId, date, sitting_id: sittingId, reason, created_by: createdBy, block_type: blockType },
      { onConflict: 'venue_id,date,sitting_id' }
    )
    .select('id, venue_id, date, sitting_id, reason, block_type')
    .single();
  if (error) throw error;
  return { id: data.id, venueId: data.venue_id, date: data.date, sittingId: data.sitting_id, reason: data.reason || '', blockType: data.block_type || 'closed' };
}

/** Remove a venue block by id. */
export async function deleteVenueBlock(id) {
  const { error } = await supabase
    .from('venue_day_blocks')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

/** Insert a single occupancy guest row */
export async function insertOccupancyGuest(guestData) {
  const row = appOccupancyToDb(guestData);
  const { data, error } = await supabase
    .from('hotel_occupancy_cache')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return dbOccupancyToApp(data);
}

/** Update an occupancy row */
export async function updateOccupancyGuest(id, updates) {
  const row = appOccupancyToDb(updates);
  const { data, error } = await supabase
    .from('hotel_occupancy_cache')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return dbOccupancyToApp(data);
}

/** Delete an occupancy row */
export async function deleteOccupancyGuest(id) {
  const { error } = await supabase
    .from('hotel_occupancy_cache')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

/** Upsert occupancy rows in bulk (used by Clock API sync) */
export async function upsertOccupancyBulk(rows) {
  const dbRows = rows.map(appOccupancyToDb);
  const { data, error } = await supabase
    .from('hotel_occupancy_cache')
    .upsert(dbRows, { onConflict: 'date,room_number' })
    .select();
  if (error) throw error;
  return (data || []).map(dbOccupancyToApp);
}

/** Delete all manual occupancy rows for a date (used when replacing with Clock sync) */
export async function clearOccupancyForDate(date) {
  const { error } = await supabase
    .from('hotel_occupancy_cache')
    .delete()
    .eq('date', date);
  if (error) throw error;
}

/** Fetch spa bookings for a specific date and room number */
export async function fetchSpaBookingsByDateRoom(date, roomNumber) {
  const { data, error } = await supabase
    .from('spa_bookings')
    .select('*')
    .eq('date', date)
    .eq('room_number', roomNumber);
  if (error) throw error;
  return (data || []).map(dbSpaToApp);
}

/** Fetch all spa bookings for a date (for allocation overview) */
export async function fetchSpaBookingsByDate(date) {
  const { data, error } = await supabase
    .from('spa_bookings')
    .select('*')
    .eq('date', date)
    .order('time', { ascending: true });
  if (error) throw error;
  return (data || []).map(dbSpaToApp);
}

/** Fetch dinner table bookings for a specific date and room number */
export async function fetchDinnerByDateRoom(date, roomNumber) {
  const { data, error } = await supabase
    .from('table_bookings')
    .select('*')
    .eq('date', date)
    .eq('room_number', roomNumber)
    .not('status', 'in', '("cancelled","no_show")');
  if (error) throw error;
  return (data || []).map(dbTableBookingToApp);
}

// ═══════════════════════════════════════════
// SEEDING: populate DB from realData.js on first run
// ═══════════════════════════════════════════

export async function seedFromRealData(realEvents, realClients) {
  // Check if events table is already populated
  const { count } = await supabase
    .from('events')
    .select('id', { count: 'exact', head: true });

  if (count && count > 0) {
    console.log(`[Supabase] Database already has ${count} events, skipping seed.`);
    return false;
  }

  console.log('[Supabase] Empty database detected — seeding with real data...');

  // Insert clients first
  if (realClients && realClients.length > 0) {
    const clientRows = realClients.map((c) => ({
      name: c.name || '',
      company: c.company || '',
      phone: c.phone || '',
      email: c.email || '',
      tag: c.tag || '',
      total_events: c.totalEvents || 0,
      total_revenue: c.totalRevenue || 0,
      last_event: c.lastEvent || null,
    }));
    const { error: cErr } = await supabase.from('clients').insert(clientRows);
    if (cErr) console.error('[Supabase] Error seeding clients:', cErr.message);
    else console.log(`[Supabase] Seeded ${clientRows.length} clients.`);
  }

  // Insert events
  if (realEvents && realEvents.length > 0) {
    const eventRows = realEvents.map((evt) => ({
      name: evt.name || '',
      client_name: evt.client || '',
      client_phone: evt.clientPhone || '',
      client_email: evt.clientEmail || '',
      type: evt.type || 'corporate',
      venue_id: evt.venue_id || '',
      date: evt.date,
      start_time: evt.startTime || '',
      end_time: evt.endTime || '',
      guests: evt.guests || 0,
      stage: evt.stage || 'enquiry',
      setup: evt.setup || '',
      equipment: evt.equipment || [],
      fb_items: evt.fbItems || [],
      services: evt.services || [],
      room_hire: evt.roomHire || 0,
      price_per_head: evt.pricePerHead || 0,
      package_total: evt.packageTotal || 0,
      deposit_paid: evt.depositPaid || 0,
      notes: evt.notes || '',
      billing_type: evt.billingType || 'group',
      consumption: evt.consumption,
      gcal_id: evt.gcal_id || '',
      gcal_calendar: evt.gcal_calendar || '',
    }));
    const { error: eErr } = await supabase.from('events').insert(eventRows);
    if (eErr) console.error('[Supabase] Error seeding events:', eErr.message);
    else console.log(`[Supabase] Seeded ${eventRows.length} events.`);
  }

  return true;
}

// ═══════════════════════════════════════════
// RESTAURANT PACKAGES (editable list: VIP, Brunch, Afternoon Tea...)
// ═══════════════════════════════════════════

function dbPackageToApp(r) {
  return {
    id: r.id,
    name: r.name || '',
    inclusions: r.inclusions || '',
    pricePp: r.price_pp != null ? parseFloat(r.price_pp) : null,
    kitchenOrderDayBefore: !!r.kitchen_order_day_before,
    active: r.active !== false,
    sortOrder: r.sort_order || 0,
  };
}

function appPackageToDb(p) {
  const row = {};
  if (p.name !== undefined) row.name = p.name;
  if (p.inclusions !== undefined) row.inclusions = p.inclusions || '';
  if (p.pricePp !== undefined) row.price_pp = p.pricePp === '' || p.pricePp == null ? null : parseFloat(p.pricePp);
  if (p.kitchenOrderDayBefore !== undefined) row.kitchen_order_day_before = !!p.kitchenOrderDayBefore;
  if (p.active !== undefined) row.active = !!p.active;
  if (p.sortOrder !== undefined) row.sort_order = parseInt(p.sortOrder, 10) || 0;
  row.updated_at = new Date().toISOString();
  return row;
}

export async function fetchRestaurantPackages() {
  const { data, error } = await supabase
    .from('restaurant_packages')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || []).map(dbPackageToApp);
}

export async function insertRestaurantPackage(pkg) {
  const { data, error } = await supabase
    .from('restaurant_packages')
    .insert(appPackageToDb(pkg))
    .select()
    .single();
  if (error) throw error;
  return dbPackageToApp(data);
}

export async function updateRestaurantPackage(id, updates) {
  const { data, error } = await supabase
    .from('restaurant_packages')
    .update(appPackageToDb(updates))
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return dbPackageToApp(data);
}
